import React, { useEffect, useState } from "react"
import { Form, Input, Label, Row, Col, FormFeedback } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import RenderBusinessType from "./renderBusinessType"
import Spinners from "components/Common/Spinner"
import toast from "react-hot-toast"
import { businessTypes, showRegistrationType } from "helpers/common"
import KYCPrevButton from "./kycPrevButton"
import { useStoreBusinessAdditionalDetailsOnboardedInvestorMutation } from "store/api/wealthpartnerapi/onboard.api"

const BusinessAdditionalDetails = ({ kycData }) => {
  const [mutate, { isLoading }] =
    useStoreBusinessAdditionalDetailsOnboardedInvestorMutation()
  const [businessType, setBusinessType] = useState()
  const [showGSTIN, setShowGSTIN] = useState(false)

  const storeDetails = async (body) => {
    try {
      const { success, message } = await mutate({
        id: kycData?.investor_id,
        ...body,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "verifySuccess" })
        window.location.reload()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      showGSTIN,
      politicalExposure: kycData?.companyAdditionalDetails?.politicalExposure,
      entity_registration_no:
        kycData?.companyAdditionalDetails?.entity_registration_no,
      constitution_of_business: businessType,
      initial_partnership_deed:
        kycData?.companyAdditionalDetails?.initial_partnership_deed,
      latest_partnership_deed:
        kycData?.companyAdditionalDetails?.latest_partnership_deed,
      certificate_of_incorporation:
        kycData?.companyAdditionalDetails?.certificate_of_incorporation,
      gstin_certificate: kycData?.companyAdditionalDetails?.gstin_certificate,
      memorandum_of_association:
        kycData?.companyAdditionalDetails?.memorandum_of_association,
      articles_of_association:
        kycData?.companyAdditionalDetails?.articles_of_association,
      initial_llp_deed: kycData?.companyAdditionalDetails?.initial_llp_deed,
      latest_llp_deed: kycData?.companyAdditionalDetails?.latest_llp_deed,
      // huf_creation_document:
      //   kycData?.companyAdditionalDetails?.huf_creation_document,
      trust_deed: kycData?.companyAdditionalDetails?.trust_deed,
      society_bye_laws: kycData?.companyAdditionalDetails?.society_bye_laws,
    },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      storeDetails(values)
    },
  })

  const politicalExposeOptions = [
    "Not Applicable",
    "Politically Exposed Person",
    "Related to Politically Exposed Person",
  ]

  useEffect(() => {
    if (kycData) {
      const bTypes = businessTypes(kycData?.investor?.entity_type)
      const type =
        validation.values.constitution_of_business ||
        kycData?.companyAdditionalDetails?.constitution_of_business ||
        null

      setBusinessType(bTypes.includes(type) ? type : null)
      const isExists = !kycData?.companyDetails?.gst_waiver
      setShowGSTIN(isExists)
    }
  }, [kycData, validation])

  return (
    <>
      <div className="bank-details-wrapper">
        <h5 className="title mb-3">Additional Details</h5>
        <p>These details are required for registration of an investor</p>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Row className="mb-2">
            <Col xl={12}>
              <Label className="col-form-label">
                Political Exposure <span className="error">*</span>
              </Label>
              <select
                id="politicalExposure"
                name="politicalExposure"
                className={`form-select pageSize ${
                  validation.touched.politicalExposure &&
                  validation.errors.politicalExposure &&
                  "is-invalid"
                }`}
                value={validation.values.politicalExposure}
                onChange={validation.handleChange}
              >
                <option value="">Select</option>
                {politicalExposeOptions.map((item, index) => (
                  <option key={`political_exposure_${index}`} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              {validation.errors.politicalExposure ? (
                <FormFeedback type="invalid">
                  {validation.errors.politicalExposure}
                </FormFeedback>
              ) : null}
            </Col>
            <Col xl={12}>
              <Label className="col-form-label">
                {`Entity Registration No.${
                  showRegistrationType(businessType)?.trim()
                    ? ` (${showRegistrationType(businessType)})`
                    : ""
                }`}
                <span className="error">*</span>
              </Label>
              <Input
                name="entity_registration_no"
                type="text"
                className="form-control"
                id="entity_registration_no"
                placeholder={`Enter Entity Registration No.`}
                value={validation.values.entity_registration_no}
                onChange={validation.handleChange}
                invalid={
                  validation.touched.entity_registration_no &&
                  validation.errors.entity_registration_no
                }
              />
              {validation.touched.entity_registration_no &&
              validation.errors.entity_registration_no ? (
                <FormFeedback type="invalid">
                  {validation.errors.entity_registration_no}
                </FormFeedback>
              ) : null}
            </Col>

            <Col xl={12}>
              <Label className="col-form-label">
                Constitution of Business
                <span className="error">*</span>
              </Label>
              <select
                id="constitution_of_business"
                // disabled
                name="constitution_of_business"
                className={`form-select pageSize ${
                  validation.touched.constitution_of_business &&
                  validation.errors.constitution_of_business &&
                  "is-invalid"
                }`}
                value={validation.values.constitution_of_business}
                onChange={validation.handleChange}
              >
                <option value={""}>Select</option>
                {businessTypes(kycData?.investor?.entity_type).map(
                  (item, index) => (
                    <option
                      key={`constitution_of_business_${index}`}
                      value={item}
                    >
                      {item}
                    </option>
                  )
                )}
              </select>
              {validation.touched.constitution_of_business &&
              validation.errors.constitution_of_business ? (
                <FormFeedback type="invalid">
                  {validation.errors.constitution_of_business}
                </FormFeedback>
              ) : null}
            </Col>

            {validation.values.constitution_of_business !==
              "Hindu Undivided Family" && (
              <Row>
                <div className="charter-documents-wrapper">
                  <h5>Charter Documents</h5>
                  <p>Upload Required Charter Documents</p>
                </div>
              </Row>
            )}

            <RenderBusinessType
              type={validation.values.constitution_of_business}
              validation={validation}
              kycData={kycData}
            />

            <Col sm={12}>
              <Row className="mt-4">
                <KYCPrevButton
                  kycData={kycData}
                  disabled={isLoading}
                  ButtonHtml={
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinners /> : "Save & Next"}
                    </button>
                  }
                />
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  )
}

export default BusinessAdditionalDetails
